/* lib/adapters/chats.js — форма структурных кадров чата между боевым API и UI прототипа.
 * Кадры стрима draft_entity/confirm_action приходят с backend-именами полей (Decimal-строки,
 * плоский preview), а feed-карточки (DraftEntityCard/ConfirmActionCard) ждут mock-форму
 * (Number, computed-объект, preview-объект). Адаптеры скрывают это расхождение. Поля,
 * которые UI читает 1:1, проходят без изменений. */

export const num = (v) => (v == null || v === '' ? null : Number(v));

// ── draft_entity (propose_entity → DraftEntityCard) ──────────────────────────
// Кадр: {type:'draft_entity', proposal_id, kind, title, payload, computed, status,
//        committed_target, rev}. payload — данные будущей сущности (как есть, редактор
//        мутирует копию). computed — производные числа (только ТТК; card/menu → null).
//        Decimal-строки в computed коэрцим в Number (cost_per_portion_rub/food_cost_pct/
//        kcal_per_portion и КБЖУ-снимки), чтобы fmt.* в карточке считал/форматировал.
function coerceComputed(c) {
  if (!c || typeof c !== 'object') return null;
  const k100 = c.kbju_per_100g && typeof c.kbju_per_100g === 'object' ? c.kbju_per_100g : null;
  const kPort = c.kbju_per_portion && typeof c.kbju_per_portion === 'object' ? c.kbju_per_portion : null;
  return {
    ...c,
    cost_per_portion_rub: num(c.cost_per_portion_rub),
    cost_per_100g_rub: num(c.cost_per_100g_rub),
    food_cost_pct: num(c.food_cost_pct),
    kcal_per_portion: num(c.kcal_per_portion),
    selling_price_rub: num(c.selling_price_rub),
    kbju_per_100g: k100,
    kbju_per_portion: kPort,
  };
}

export function adaptDraftEntity(frame) {
  const f = frame || {};
  // for_menu карточка прототипа берётся из draft.for_menu (бэк кладёт в payload.for_menu).
  const forMenu = (f.payload && typeof f.payload === 'object' && f.payload.for_menu) || f.for_menu || null;
  const fm = forMenu
    ? { ...forMenu, menu_title: forMenu.menu_title || forMenu.dish_name || '' }
    : null;
  // F40 W4: версионная линия. Legacy-черновики без version_* → линия из одной
  // версии (version_group_id := proposal_id, version_n := (rev||0)+1). Источник
  // фолбэка зеркалит бэк draft_group_id / build_draft_version.
  return {
    proposal_id: f.proposal_id,
    version_group_id: f.version_group_id || f.proposal_id,
    version_n: f.version_n || (f.rev || 0) + 1,
    superseded: !!f.superseded,
    kind: f.kind,
    title: f.title || '',
    payload: (f.payload && typeof f.payload === 'object') ? f.payload : {},
    computed: coerceComputed(f.computed),
    status: f.status || 'draft',
    committed_target: f.committed_target || null,
    rev: f.rev || 0,
    for_menu: fm,
  };
}

// ── confirm_action (propose_*_edit → ConfirmActionCard) ──────────────────────
// Кадр: {type:'confirm_action', pending_id, kind, target_id, title, ...preview}.
// preview спредится в КОРЕНЬ кадра (entity_edits._build_*_preview): summary, cost_before/
// cost_after, selling_before/selling_after, fc_before/fc_after, rows_before/rows_after.
// Карточка прототипа читает pc.preview.{cost_before,cost_after,fc_pct_before|fc_before,
// fc_pct_after|fc_after,selling_price_before|price_before,selling_price_after|price_after}.
// Поэтому собираем preview-ОБЪЕКТ из плоских полей кадра, Decimal-строки → Number, и
// нормализуем fc/selling под обе схемы именования (карточка фолбэчит, но даём канон).
export function adaptConfirmAction(frame) {
  const f = frame || {};
  const costBefore = num(f.cost_before);
  const costAfter = num(f.cost_after);
  const fcBefore = num(f.fc_pct_before != null ? f.fc_pct_before : f.fc_before);
  const fcAfter = num(f.fc_pct_after != null ? f.fc_pct_after : f.fc_after);
  const sellBefore = num(
    f.selling_price_before != null ? f.selling_price_before
      : f.selling_before != null ? f.selling_before : f.price_before,
  );
  const sellAfter = num(
    f.selling_price_after != null ? f.selling_price_after
      : f.selling_after != null ? f.selling_after : f.price_after,
  );
  return {
    id: f.pending_id,
    kind: f.kind,
    target_id: f.target_id,
    title: f.title || '',
    status: 'pending',
    preview: {
      summary: f.summary || '',
      cost_before: costBefore,
      cost_after: costAfter,
      // канон + алиасы — карточка читает fc_pct_*/selling_price_* с фолбэком на fc_*/price_*
      fc_pct_before: fcBefore,
      fc_pct_after: fcAfter,
      selling_price_before: sellBefore,
      selling_price_after: sellAfter,
      rows_before: f.rows_before ?? null,
      rows_after: f.rows_after ?? null,
      // Структурный диф «было → станет» (entity_edits._*_changes) — рендерит
      // канвас изменений ChangeCanvas; компакт-карточка его не читает.
      changes: (f.changes && typeof f.changes === 'object') ? f.changes : null,
      // Действие (kind 'action'): подпись и ссылка приезжают в превью.
      action: f.action || null,
      action_label: f.action_label || null,
      href: f.href || null,
      // Пакетное действие: строки с галочками (канвас) и признак выбора.
      items: Array.isArray(f.items) ? f.items : null,
      selectable: !!f.selectable,
    },
  };
}

// ── canvas-спека из metadata сообщения (structured ready) ────────────────────
// Бэк кладёт ttk_id / menu_id / report_id в metadata assistant-сообщения при ready
// (Workspace.selectChat восстанавливает канвас по ним). Канонизируем в {type,id}.
// reportType — функция slug→тип ('foodcost'|'abc'|'inventory') по skill-привязке отчёта.
export function adaptCanvasSpec(metadata, reportType) {
  if (!metadata || typeof metadata !== 'object') return null;
  if (metadata.canvas && metadata.canvas.type) return metadata.canvas;
  if (metadata.ttk_id != null) return { type: 'ttk', id: metadata.ttk_id };
  if (metadata.menu_id != null) return { type: 'menu', id: metadata.menu_id };
  if (metadata.report_id != null) {
    const t = (typeof reportType === 'function' ? reportType(metadata.report_id) : null)
      || metadata.report_type || null;
    const map = {
      foodcost: 'foodcost', abc: 'abc', inventory: 'inventory',
      'foodcost-optimize': 'foodcost', 'abc-analyze': 'abc', 'inventory-analyze': 'inventory',
    };
    return { type: map[t] || 'abc', id: metadata.report_id };
  }
  return null;
}

// ── progressive canvas preview (chat-2) ──────────────────────────────────────
// Бэк стримит СЫРЫЕ фрагменты JSON (ttk_partial/menu_partial). Клиент копит их и
// partial-парсит (partial-json, Allow.ALL); здесь мапим РЕАЛЬНУЮ схему
// (ttk.schemas.TTKResponse.draft / MenuDesignResponse) в preview-форму канвасов
// прототипа (TTKCanvas → ingredients_preview, MenuCanvas → sections_preview).
// Отчёты — без field-preview (числа мид-стрим бессмысленны → только скелет).
export function partialCanvasPreview(kind, parsed) {
  if (!parsed || typeof parsed !== 'object') return null;
  if (kind === 'ttk') {
    const draft = (parsed.draft && typeof parsed.draft === 'object') ? parsed.draft : {};
    const names = (Array.isArray(draft.ingredients) ? draft.ingredients : [])
      .map((i) => i && i.name).filter(Boolean);
    return { title: draft.dish_name || '', ingredients_preview: names };
  }
  if (kind === 'menu') {
    const positions = Array.isArray(parsed.positions) ? parsed.positions : [];
    const cats = [];
    const seen = new Set();
    const names = [];
    for (const p of positions) {
      if (p && p.category && !seen.has(p.category)) { seen.add(p.category); cats.push(p.category); }
      if (p && p.name) names.push(p.name);
    }
    return { title: parsed.menu_name || '', sections_preview: cats.length ? cats : names.slice(0, 8) };
  }
  return null; // foodcost/abc/inventory — только скелет
}

// ── skills (/api/chats/skills/ → карточки EmptyState + справка) ───────────────
// Бэк отдаёт {slug,name,hint,starter_hint,description,inputs,outputs,examples}.
// Карточке нужны {slug,icon,name,hint,examplePrompt}; справочная панель чата —
// description/inputs/outputs/examples (что отправить / что сделает Ресториум /
// примеры запросов). icon на бэке нет — мапим по slug.
const SKILL_ICON = {
  'ttk-generate': 'book',
  'menu-design': 'reports',
  'foodcost-optimize': 'percent',
  'abc-analyze': 'analytics',
  'inventory-analyze': 'box',
};
export function adaptSkillCard(s) {
  const examples = Array.isArray(s.examples) ? s.examples : [];
  return {
    slug: s.slug,
    icon: SKILL_ICON[s.slug] || 'sparkles',
    name: s.name || s.slug,
    hint: s.hint || '',
    starter_hint: s.starter_hint || '',
    description: s.description || '',
    inputs: Array.isArray(s.inputs) ? s.inputs : [],
    outputs: Array.isArray(s.outputs) ? s.outputs : [],
    examples,
    examplePrompt: examples[0] || s.starter_hint || s.name || '',
  };
}
